import apiClient from './client';
import type { Campaign, CampaignDetail, AudienceMetricsResponse } from '@/types/campaign';
import type { CreateAdGroupRequest, CreateAdGroupResponse } from '@/types/adGroup';
import type { CreateAssetGroupRequest, CreateAssetGroupResponse } from '@/types/assetGroup';

interface CampaignsResponse {
  campaigns: Campaign[];
  total: number;
  page: number;
  per_page: number;
}

// Entity status update types
export type EntityStatusAction = 'pause' | 'enable' | 'remove';

export interface UpdateEntityStatusResponse {
  success: boolean;
  message: string;
  new_status?: string;
  entity_id?: string;
  entity_type?: 'ad_group' | 'asset_group' | 'ad';
}

export const campaignsApi = {
  async getCampaigns(useCached?: boolean): Promise<Campaign[]> {
    // member_uuid is no longer needed - backend uses JWT authentication
    const response = await apiClient.get<CampaignsResponse>('/campaigns', {
      params: useCached ? { use_cached: true } : undefined
    });
    return response.data.campaigns;
  },

  async getCampaign(campaignId: string, useCached?: boolean): Promise<CampaignDetail> {
    const response = await apiClient.get<CampaignDetail>(`/campaigns/${campaignId}`, {
      params: useCached ? { use_cached: true } : undefined
    });
    return response.data;
  },

  async updateCampaignBudget(campaignId: string, budget: number): Promise<void> {
    await apiClient.patch(`/campaigns/${campaignId}/budget`, { budget });
  },

  async createAdGroup(
    campaignId: string,
    request: CreateAdGroupRequest
  ): Promise<CreateAdGroupResponse> {
    const response = await apiClient.post<CreateAdGroupResponse>(
      `/campaigns/${campaignId}/ad-groups`,
      request
    );
    return response.data;
  },

  /**
   * Create a new asset group for a Performance Max campaign.
   *
   * This endpoint creates an asset group with all required assets:
   * - Text assets (headlines, descriptions, long headline, business name)
   * - Image assets (landscape, square, logo - uploaded from Media Library)
   *
   * Uses a longer timeout since image uploads to Google Ads can take time.
   */
  async createAssetGroup(
    campaignId: string,
    request: CreateAssetGroupRequest
  ): Promise<CreateAssetGroupResponse> {
    const response = await apiClient.post<CreateAssetGroupResponse>(
      `/campaigns/${campaignId}/asset-groups`,
      request,
      { timeout: 180000 } // 3 minutes for image uploads + API calls
    );
    return response.data;
  },

  // ============================================================
  // Entity Status Update Methods
  // ============================================================

  /**
   * Update the status of a campaign.
   *
   * @param campaignId - The campaign ID
   * @param action - Action to perform: 'pause', 'enable', or 'remove'
   * @param skipDataSync - If true, skip triggering data sync (for batched operations)
   * @returns The update response with success status and new status
   */
  async updateCampaignStatus(
    campaignId: string,
    action: EntityStatusAction,
    skipDataSync = false
  ): Promise<UpdateEntityStatusResponse> {
    const response = await apiClient.post<UpdateEntityStatusResponse>(
      `/campaigns/${campaignId}/status`,
      { action, skip_data_sync: skipDataSync }
    );
    return response.data;
  },

  /**
   * Update the status of an ad group.
   *
   * @param campaignId - The campaign ID
   * @param adGroupId - The ad group ID
   * @param action - Action to perform: 'pause', 'enable', or 'remove'
   * @param skipDataSync - If true, skip triggering data sync (for batched operations)
   * @returns The update response with success status and new status
   */
  async updateAdGroupStatus(
    campaignId: string,
    adGroupId: string,
    action: EntityStatusAction,
    skipDataSync = false
  ): Promise<UpdateEntityStatusResponse> {
    const response = await apiClient.post<UpdateEntityStatusResponse>(
      `/campaigns/${campaignId}/ad-groups/${adGroupId}/status`,
      { action, skip_data_sync: skipDataSync }
    );
    return response.data;
  },

  /**
   * Update the status of an asset group (Performance Max campaigns).
   *
   * @param campaignId - The campaign ID
   * @param assetGroupId - The asset group ID
   * @param action - Action to perform: 'pause', 'enable', or 'remove'
   * @param skipDataSync - If true, skip triggering data sync (for batched operations)
   * @returns The update response with success status and new status
   */
  async updateAssetGroupStatus(
    campaignId: string,
    assetGroupId: string,
    action: EntityStatusAction,
    skipDataSync = false
  ): Promise<UpdateEntityStatusResponse> {
    const response = await apiClient.post<UpdateEntityStatusResponse>(
      `/campaigns/${campaignId}/asset-groups/${assetGroupId}/status`,
      { action, skip_data_sync: skipDataSync }
    );
    return response.data;
  },

  /**
   * Update the status of an ad.
   *
   * @param campaignId - The campaign ID
   * @param adGroupId - The ad group ID
   * @param adId - The ad ID
   * @param action - Action to perform: 'pause', 'enable', or 'remove'
   * @param skipDataSync - If true, skip triggering data sync (for batched operations)
   * @returns The update response with success status and new status
   */
  async updateAdStatus(
    campaignId: string,
    adGroupId: string,
    adId: string,
    action: EntityStatusAction,
    skipDataSync = false
  ): Promise<UpdateEntityStatusResponse> {
    const response = await apiClient.post<UpdateEntityStatusResponse>(
      `/campaigns/${campaignId}/ad-groups/${adGroupId}/ads/${adId}/status`,
      { action, skip_data_sync: skipDataSync }
    );
    return response.data;
  },

  /**
   * Update channel control settings for a Demand Gen ad group.
   *
   * @param campaignId - The campaign ID
   * @param adGroupId - The ad group ID
   * @param channelSettings - The channel settings to apply
   * @param skipDataSync - If true, skip triggering data sync (for batched operations)
   * @returns The update response
   */
  async updateAdGroupChannelSettings(
    campaignId: string,
    adGroupId: string,
    channelSettings: {
      youtube_in_stream: boolean;
      youtube_in_feed: boolean;
      youtube_shorts: boolean;
      discover: boolean;
      gmail: boolean;
      display: boolean;
      // maps: boolean;  // Not yet supported in Google Ads API v22/v23
    },
    skipDataSync = false
  ): Promise<{ success: boolean; message: string; ad_group_id: string; channel_settings: Record<string, boolean> }> {
    const response = await apiClient.put(
      `/campaigns/${campaignId}/ad-groups/${adGroupId}/channels`,
      channelSettings,
      { params: { skip_data_sync: skipDataSync } }
    );
    return response.data;
  },

  /**
   * Get audience performance metrics for a campaign (30-day data).
   * Only available for Demand Gen and other audience-based campaigns.
   */
  async getAudienceMetrics(campaignId: string): Promise<AudienceMetricsResponse> {
    const response = await apiClient.get(`/campaigns/${campaignId}/audience-metrics`);
    return response.data;
  },

  // ============================================================
  // Audience Targeting Methods (Demand Gen campaigns)
  // ============================================================

  /**
   * Get available audiences for targeting in a campaign.
   * Returns user lists, custom audiences, and combined audiences.
   */
  async getAvailableAudiences(campaignId: string, options?: {
    search?: string;
    type?: string;
  }): Promise<{
    audiences: Array<{
      resource_name: string;
      audience_id: string;
      audience_name: string;
      audience_type: string;
      description?: string;
    }>;
    total: number;
  }> {
    const response = await apiClient.get(`/campaigns/${campaignId}/available-audiences`, {
      params: options,
    });
    return response.data;
  },

  /**
   * Add an audience to an ad group for targeting.
   *
   * @param campaignId - The campaign ID
   * @param adGroupId - The ad group ID
   * @param audienceResourceName - The audience resource name (e.g., "customers/123/userLists/456")
   * @param bidModifier - Optional bid modifier (default 1.0 = no adjustment)
   */
  async addAudienceToAdGroup(
    campaignId: string,
    adGroupId: string,
    audienceResourceName: string,
    bidModifier?: number
  ): Promise<{
    success: boolean;
    criterion_id: string;
    message: string;
  }> {
    const response = await apiClient.post(
      `/campaigns/${campaignId}/ad-groups/${adGroupId}/audiences`,
      {
        audience_resource_name: audienceResourceName,
        bid_modifier: bidModifier,
      }
    );
    return response.data;
  },

  /**
   * Add multiple audiences to an ad group in a single batch operation.
   * More efficient than calling addAudienceToAdGroup multiple times.
   *
   * @param campaignId - The campaign ID
   * @param adGroupId - The ad group ID
   * @param audiences - Array of audiences to add with resource_name and optional bid_modifier
   */
  async addAudiencesToAdGroup(
    campaignId: string,
    adGroupId: string,
    audiences: Array<{ audience_resource_name: string; bid_modifier?: number }>
  ): Promise<{
    success: boolean;
    added_count: number;
    failed_count: number;
    criterion_ids: string[];
    message: string;
  }> {
    const response = await apiClient.post(
      `/campaigns/${campaignId}/ad-groups/${adGroupId}/audiences/batch`,
      { audiences }
    );
    return response.data;
  },

  /**
   * Remove an audience from an ad group.
   *
   * @param campaignId - The campaign ID
   * @param adGroupId - The ad group ID
   * @param criterionId - The criterion ID of the audience to remove
   */
  async removeAudienceFromAdGroup(
    campaignId: string,
    adGroupId: string,
    criterionId: string
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await apiClient.delete(
      `/campaigns/${campaignId}/ad-groups/${adGroupId}/audiences/${criterionId}`
    );
    return response.data;
  },

  /**
   * Add demographic targeting criteria to an ad group.
   * Only available for Demand Gen campaigns.
   * This is an additive operation - existing criteria are preserved.
   *
   * @param campaignId - The campaign ID
   * @param adGroupId - The ad group ID
   * @param demographics - The demographic criteria to add
   */
  async addDemographicTargeting(
    campaignId: string,
    adGroupId: string,
    demographics: {
      age_ranges?: string[];
      genders?: string[];
      income_ranges?: string[];
      parental_statuses?: string[];
    }
  ): Promise<{
    success: boolean;
    message: string;
    added_count: number;
    removed_count: number;
  }> {
    const response = await apiClient.post(
      `/campaigns/${campaignId}/ad-groups/${adGroupId}/demographics`,
      demographics
    );
    return response.data;
  },
};